import { type InfiniteData } from '@tanstack/react-query';
import produce from 'immer';
import { merge, omit } from 'lodash';

import { type ListResponse, type ShortUrlResponse } from '@air/api/types';

import { type ShortUrlUpdateParams } from './useUpdateShortUrlMutation';

export const updateShortUrlInListCache = (
  shortId: string,
  updateParams: ShortUrlUpdateParams,
  currentCache: InfiniteData<ListResponse<ShortUrlResponse>>,
  options?: { newId?: string },
) =>
  produce(currentCache, (draft) => {
    for (const page of draft.pages) {
      if (!page?.data) {
        continue;
      }

      const itemIndex = page.data.findIndex((item) => item.id === shortId);
      if (itemIndex !== -1) {
        const responseFields = omit(updateParams, ['workspaceId', 'shortId']);
        page.data[itemIndex] = merge({}, page.data[itemIndex], responseFields, {
          ...(options?.newId && { id: options.newId }),
        });
        break;
      }
    }
  });

export const removeShortUrlFromListCache = (
  shortId: string,
  currentCache: InfiniteData<ListResponse<ShortUrlResponse>>,
) =>
  produce(currentCache, (draft) => {
    for (const page of draft.pages) {
      if (!page?.data) {
        continue;
      }

      const itemIndex = page.data.findIndex((item) => item.id === shortId);
      if (itemIndex !== -1) {
        page.data.splice(itemIndex, 1);
        // Update the total count if it exists
        if (page.total !== undefined) {
          page.total = Math.max(0, page.total - 1);
        }
        break;
      }
    }
  });

export const updateCollectionNameInListCache = (
  collectionId: string,
  updates: { name?: string },
  currentCache: InfiniteData<ListResponse<ShortUrlResponse>>,
) =>
  produce(currentCache, (draft) => {
    if (!updates.name) {
      return draft;
    }

    for (const page of draft.pages) {
      if (!page?.data) {
        continue;
      }

      page.data.forEach((item, index) => {
        if (!item || typeof item !== 'object') {
          return;
        }

        // Narrow down to the collection item
        const itemCollection = 'collection' in item ? item.collection : undefined;
        if (
          itemCollection &&
          typeof itemCollection === 'object' &&
          'id' in itemCollection &&
          itemCollection.id === collectionId
        ) {
          page.data[index] = merge({}, item, {
            collection: { ...itemCollection, name: updates.name },
          });
        }
      });
    }
  });
